import {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useReducedMotion} from "framer-motion";

import {IRange} from "@pg-design/range-module";
import {useFormRHF} from "@pg-design/rhf-components-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {useMatchedView} from "@/features/km_app/hooks/use_matched_view";
import {addNotification} from "@/features/notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "@/features/notifications/utils/error_notifications";
import {getAmplitudeButtonCategoryByView} from "@/features/tracking/amplitude/get_amplitude_button_category_by_view";
import {getAmplitudeLocationByView} from "@/features/tracking/amplitude/get_amplitude_location_by_view";
import {
    getHomeMatchAmplitudeLocationByPreferencesSource,
    HomeMatchAmplitudeTracker,
    HomeMatchPreferencesSurveyAmplitudeButton
} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {useGetUserHomeMatchPreferencesQuery} from "@/features/user/api/user_api";
import {useHomeMatchPreferences} from "@/features/user/hooks/use_home_match_preferences";
import {useAppDispatch} from "@/store/hooks/use_app_dispatch";

import {useGetHomeMatchPreferencesQuery} from "../../api/get_home_match_preferences";
import {usePatchHomeMatchPreferencesMutation} from "../../api/patch_home_match_preferences";
import {DEFAULT_HOME_MATCH_BASIC_CONSENT} from "../../constants/home_match_survey_defaults";
import {HOME_MATCH_PREFERENCES_SURVEY_CONTENT} from "../../constants/preferences/home_match_preferences_survey_content";
import {HomeMatchPreferencesMarketType} from "../../constants/preferences/home_match_preferences_values";
import {IHomeMatchPreferencesSurveyForm} from "../../types/home_match_preferences";
import {buildHomeMatchPreferencesSurveyPatch} from "../../utils/build_home_match_preferences_survey_patch";
import {getHomeMatchConfigurationSourceSection, HomeMatchConfigurationSourceSectionTrigger} from "../../utils/get_home_match_configuration_source_section";
import {getHomeMatchPreferencesSurveyFormValues} from "../../utils/get_home_match_preferences_survey_form_values";
import {hasHomeMatchRegion} from "../../utils/get_home_match_preferences_wizard_step_patch";
import {HomeMatchPreferencesWizardSurveyStep} from "../../utils/get_home_match_preferences_wizard_steps";
import {IHomeMatchPreferencesWizardSummaryData} from "../../utils/get_home_match_preferences_wizard_summary_labels";
import {useHomeMatchPreferencesWithRegions} from "./use_home_match_preferences_with_regions";
import {IHomeMatchPreferencesWizardMapActions} from "./use_home_match_preferences_wizard_location_map";

const {SAVE_ERROR, SAVE_SUCCESS} = HOME_MATCH_PREFERENCES_SURVEY_CONTENT;
const SUMMARY_RETURN_MS = 220;
const SUMMARY_RETURN_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

const toOptionalRange = (range: IRange<number> | null | undefined) => {
    if (range?.lower == null && range?.upper == null) return null;

    return {lower: range?.lower ?? 0, upper: range?.upper ?? 0};
};

interface IParams {
    isOpen: boolean;
    onClose: () => void;
    preferencesSurveySource?: PreferencesSurveySource;
}

export const useHomeMatchPreferencesSurveyModal = (params: IParams) => {
    const {isOpen, onClose, preferencesSurveySource = PreferencesSurveySource.PREFERENCES} = params;
    const dispatch = useAppDispatch();
    const view = useMatchedView();
    const location = getAmplitudeLocationByView(view) ?? null;
    const buttonCategory = getAmplitudeButtonCategoryByView(view);
    const {isLoading, preferences, refetchHomeMatchPreferences, regionDetails} = useHomeMatchPreferencesWithRegions();
    const {data: survey} = useGetHomeMatchPreferencesQuery();
    const {data: homeMatchPreferences} = useGetUserHomeMatchPreferencesQuery();
    const [patchPreferences, {isLoading: isSaving}] = usePatchHomeMatchPreferencesMutation();
    const {isSwitchingHomeMatchPreferences, switchHomeMatchPreferences} = useHomeMatchPreferences();
    const defaultValues = useMemo(() => getHomeMatchPreferencesSurveyFormValues(preferences, survey), [preferences, survey]);
    const methods = useFormRHF<IHomeMatchPreferencesSurveyForm>({defaultValues});
    const {formState, handleSubmit, reset} = methods;
    const canSave = hasHomeMatchRegion(methods.watch("regions") ?? []);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [openedFromSummary, setOpenedFromSummary] = useState(false);
    const [summaryReturnCount, setSummaryReturnCount] = useState(0);
    const summarySnapshotRef = useRef<IHomeMatchPreferencesSurveyForm | null>(null);
    const [homeMatchBasicConsent, setHomeMatchBasicConsent] = useState(DEFAULT_HOME_MATCH_BASIC_CONSENT);
    const [isExitOpen, setIsExitOpen] = useState(false);
    const [parameterStep, setParameterStep] = useState<HomeMatchPreferencesWizardSurveyStep | null>(null);
    const [isLocationMapOpen, setIsLocationMapOpen] = useState(false);
    const [locationMapActions, setLocationMapActions] = useState<IHomeMatchPreferencesWizardMapActions | null>(null);
    const wasOpen = useRef(false);
    const exitButtonRef = useRef(HomeMatchPreferencesSurveyAmplitudeButton.CLOSE);
    const summaryContentRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    useEffect(() => {
        if (!isOpen) {
            wasOpen.current = false;
            setIsSummaryOpen(false);
            setOpenedFromSummary(false);
            summarySnapshotRef.current = null;
            setParameterStep(null);
            setIsLocationMapOpen(false);
            setLocationMapActions(null);
            return;
        }

        const justOpened = !wasOpen.current;

        wasOpen.current = true;

        if (!justOpened) return;

        reset(defaultValues);
        void refetchHomeMatchPreferences();
        HomeMatchAmplitudeTracker.preferencesSurveyView(location);
    }, [defaultValues, isOpen, location, refetchHomeMatchPreferences, reset]);

    useEffect(() => {
        if (!isOpen || formState.isDirty) return;

        reset(defaultValues);
    }, [defaultValues, formState.isDirty, isOpen, reset]);

    useLayoutEffect(() => {
        const content = summaryContentRef.current;

        if (summaryReturnCount === 0 || !content || shouldReduceMotion || typeof content.animate !== "function") return;

        content.animate(
            [
                {opacity: 0, transform: "translateY(0.8rem)"},
                {opacity: 1, transform: "translateY(0)"}
            ],
            {duration: SUMMARY_RETURN_MS, easing: SUMMARY_RETURN_EASING}
        );
    }, [shouldReduceMotion, summaryReturnCount]);

    const openExit = (button: HomeMatchPreferencesSurveyAmplitudeButton) => {
        exitButtonRef.current = button;
        setIsExitOpen(true);
    };

    const closeExit = () => setIsExitOpen(false);

    const dismissExit = () => {
        HomeMatchAmplitudeTracker.preferencesSurveyClick(exitButtonRef.current, buttonCategory, location);
        onClose();
    };

    const handleAfterClose = () => setIsExitOpen(false);

    const onSubmit = async (form: IHomeMatchPreferencesSurveyForm) => {
        if (!hasHomeMatchRegion(form.regions)) return;

        HomeMatchAmplitudeTracker.preferencesSurveyClick(HomeMatchPreferencesSurveyAmplitudeButton.SAVE, buttonCategory, location);

        try {
            const saved = await patchPreferences(
                buildHomeMatchPreferencesSurveyPatch({
                    additionalExpectationsDirty: Boolean(formState.dirtyFields.additionalExpectations),
                    areaDirty: Boolean(formState.dirtyFields.area),
                    buildingHeightDirty: Boolean(formState.dirtyFields.buildingHeight),
                    constructionEndDateDirty: Boolean(formState.dirtyFields.constructionEndDate),
                    floorsPreferenceDirty: Boolean(formState.dirtyFields.floorsPreference),
                    form,
                    propertyTypeDirty: Boolean(formState.dirtyFields.propertyType),
                    secondaryMarketDirty: Boolean(formState.dirtyFields.secondaryMarket),
                    source: preferencesSurveySource
                })
            ).unwrap();

            HomeMatchAmplitudeTracker.preferencesSurveySaved({
                form,
                screen: location ? {location} : null,
                source: preferencesSurveySource,
                saved: {
                    area: saved.area,
                    construction_end_date: saved.construction_end_date,
                    financing_preference: saved.financing_preference ?? undefined,
                    price: saved.price,
                    rooms: saved.rooms
                }
            });
            reset(form);

            if (homeMatchPreferences?.is_active) {
                onClose();
                dispatch(addNotification({type: "success", content: SAVE_SUCCESS}));
                return;
            }

            setHomeMatchBasicConsent(DEFAULT_HOME_MATCH_BASIC_CONSENT);
            setIsExitOpen(false);
            setIsSummaryOpen(true);
        } catch {
            dispatch(addNotification({type: "failure", content: SAVE_ERROR}));
        }
    };

    const closeSummary = () => {
        setIsSummaryOpen(false);
        onClose();
    };

    const confirmSummary = () => {
        if (isSwitchingHomeMatchPreferences) return;

        void switchHomeMatchPreferences(homeMatchBasicConsent, {
            onPhoneVerificationRequired: () => dispatch(addNotification(defaultAPIErrorNotification)),
            showSuccessNotification: false,
            skipPhoneVerificationPrompt: true,
            sourceSection: getHomeMatchConfigurationSourceSection({
                preferencesSurveySource,
                trigger: HomeMatchConfigurationSourceSectionTrigger.SUMMARY_MODAL
            }),
            syncCplPreferences: false
        }).then((isSaved) => {
            if (isSaved) closeSummary();
        });
    };

    const openSurveyFromSummary = () => {
        summarySnapshotRef.current = methods.getValues();
        setOpenedFromSummary(true);
        setIsSummaryOpen(false);
    };

    const returnToSummary = (button: HomeMatchPreferencesSurveyAmplitudeButton) => {
        HomeMatchAmplitudeTracker.preferencesSurveyClick(button, buttonCategory, location);

        if (summarySnapshotRef.current) reset(summarySnapshotRef.current);

        summarySnapshotRef.current = null;
        setOpenedFromSummary(false);
        setIsExitOpen(false);
        setIsSummaryOpen(true);
        setSummaryReturnCount((count) => count + 1);
    };

    const leaveSurvey = (button: HomeMatchPreferencesSurveyAmplitudeButton, returnsToSummary: boolean) => {
        if (returnsToSummary) {
            returnToSummary(button);
            return;
        }

        if (!isSummaryOpen && !formState.isDirty) {
            exitButtonRef.current = button;
            dismissExit();
            return;
        }

        openExit(button);
    };

    const requestClose = () => leaveSurvey(HomeMatchPreferencesSurveyAmplitudeButton.CLOSE, !isSummaryOpen && openedFromSummary);
    const discardSurvey = () => leaveSurvey(HomeMatchPreferencesSurveyAmplitudeButton.DISCARD, openedFromSummary);

    const editSummaryStep = (step: HomeMatchPreferencesWizardSurveyStep) => setParameterStep(step);

    const closeParameterStep = () => setParameterStep(null);
    const formValues = methods.getValues();
    const summary: IHomeMatchPreferencesWizardSummaryData = {
        additionalAreas: formValues.additionalExpectations,
        area: toOptionalRange(formValues.area),
        buildingHeight: formValues.buildingHeight,
        constructionEndDate: formValues.constructionEndDate,
        floorsPreference: formValues.floorsPreference,
        marketTypes: formValues.secondaryMarket
            ? [HomeMatchPreferencesMarketType.PRIMARY, HomeMatchPreferencesMarketType.SECONDARY]
            : (survey?.market_types ?? null),
        price: toOptionalRange(formValues.price),
        regionDetails,
        regionIds: formValues.regions.map((region) => Number(region.value)),
        regionOptions: formValues.regions,
        rooms: formValues.rooms,
        types: formValues.propertyType == null ? null : [formValues.propertyType]
    };

    return {
        canSave,
        closeExit,
        closeParameterStep,
        confirmSummary,
        discardSurvey,
        dismissExit,
        editSummaryStep,
        handleAfterClose,
        handleSubmit,
        homeMatchBasicConsent,
        isExitOpen,
        isLoading,
        isLocationMapOpen,
        isSaving,
        isSummaryOpen,
        isSwitchingHomeMatchPreferences,
        locationMapActions,
        methods,
        onSubmit,
        openSurveyFromSummary,
        parameterStep,
        parameterStepLocation: getHomeMatchAmplitudeLocationByPreferencesSource(preferencesSurveySource),
        requestClose,
        setHomeMatchBasicConsent,
        setIsLocationMapOpen,
        setLocationMapActions,
        summary,
        summaryContentRef
    };
};
import {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useReducedMotion} from "framer-motion";

import {IRange} from "@pg-design/range-module";
import {useFormRHF} from "@pg-design/rhf-components-module";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {useMatchedView} from "@/features/km_app/hooks/use_matched_view";
import {addNotification} from "@/features/notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "@/features/notifications/utils/error_notifications";
import {getAmplitudeButtonCategoryByView} from "@/features/tracking/amplitude/get_amplitude_button_category_by_view";
import {getAmplitudeLocationByView} from "@/features/tracking/amplitude/get_amplitude_location_by_view";
import {
    getHomeMatchAmplitudeLocationByPreferencesSource,
    HomeMatchAmplitudeTracker,
    HomeMatchPreferencesSurveyAmplitudeButton
} from "@/features/tracking/amplitude/home_match_amplitude_tracker";
import {useGetUserHomeMatchPreferencesQuery} from "@/features/user/api/user_api";
import {useHomeMatchPreferences} from "@/features/user/hooks/use_home_match_preferences";
import {useAppDispatch} from "@/store/hooks/use_app_dispatch";

import {useGetHomeMatchPreferencesQuery} from "../../api/get_home_match_preferences";
import {usePatchHomeMatchPreferencesMutation} from "../../api/patch_home_match_preferences";
import {DEFAULT_HOME_MATCH_BASIC_CONSENT} from "../../constants/home_match_survey_defaults";
import {HOME_MATCH_PREFERENCES_SURVEY_CONTENT} from "../../constants/preferences/home_match_preferences_survey_content";
import {HomeMatchPreferencesMarketType} from "../../constants/preferences/home_match_preferences_values";
import {IHomeMatchPreferencesSurveyForm} from "../../types/home_match_preferences";
import {buildHomeMatchPreferencesSurveyPatch} from "../../utils/build_home_match_preferences_survey_patch";
import {getHomeMatchConfigurationSourceSection, HomeMatchConfigurationSourceSectionTrigger} from "../../utils/get_home_match_configuration_source_section";
import {getHomeMatchPreferencesSurveyFormValues} from "../../utils/get_home_match_preferences_survey_form_values";
import {hasHomeMatchRegion} from "../../utils/get_home_match_preferences_wizard_step_patch";
import {HomeMatchPreferencesWizardSurveyStep} from "../../utils/get_home_match_preferences_wizard_steps";
import {IHomeMatchPreferencesWizardSummaryData} from "../../utils/get_home_match_preferences_wizard_summary_labels";
import {useHomeMatchPreferencesWithRegions} from "./use_home_match_preferences_with_regions";
import {IHomeMatchPreferencesWizardMapActions} from "./use_home_match_preferences_wizard_location_map";

const {SAVE_ERROR, SAVE_SUCCESS} = HOME_MATCH_PREFERENCES_SURVEY_CONTENT;
const SUMMARY_RETURN_MS = 220;
const SUMMARY_RETURN_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

const toOptionalRange = (range: IRange<number> | null | undefined) => {
    if (range?.lower == null && range?.upper == null) return null;

    return {lower: range?.lower ?? 0, upper: range?.upper ?? 0};
};

interface IParams {
    isOpen: boolean;
    onClose: () => void;
    preferencesSurveySource?: PreferencesSurveySource;
}

export const useHomeMatchPreferencesSurveyModal = (params: IParams) => {
    const {isOpen, onClose, preferencesSurveySource = PreferencesSurveySource.PREFERENCES} = params;
    const dispatch = useAppDispatch();
    const view = useMatchedView();
    const location = getAmplitudeLocationByView(view) ?? null;
    const buttonCategory = getAmplitudeButtonCategoryByView(view);
    const {isLoading, preferences, refetchHomeMatchPreferences, regionDetails} = useHomeMatchPreferencesWithRegions();
    const {data: survey} = useGetHomeMatchPreferencesQuery();
    const {data: homeMatchPreferences} = useGetUserHomeMatchPreferencesQuery();
    const [patchPreferences, {isLoading: isSaving}] = usePatchHomeMatchPreferencesMutation();
    const {isSwitchingHomeMatchPreferences, switchHomeMatchPreferences} = useHomeMatchPreferences();
    const defaultValues = useMemo(() => getHomeMatchPreferencesSurveyFormValues(preferences, survey), [preferences, survey]);
    const methods = useFormRHF<IHomeMatchPreferencesSurveyForm>({defaultValues});
    const {formState, handleSubmit, reset} = methods;
    const canSave = hasHomeMatchRegion(methods.watch("regions") ?? []);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [openedFromSummary, setOpenedFromSummary] = useState(false);
    const [summaryReturnCount, setSummaryReturnCount] = useState(0);
    const summarySnapshotRef = useRef<IHomeMatchPreferencesSurveyForm | null>(null);
    const [homeMatchBasicConsent, setHomeMatchBasicConsent] = useState(DEFAULT_HOME_MATCH_BASIC_CONSENT);
    const [isExitOpen, setIsExitOpen] = useState(false);
    const [parameterStep, setParameterStep] = useState<HomeMatchPreferencesWizardSurveyStep | null>(null);
    const [isLocationMapOpen, setIsLocationMapOpen] = useState(false);
    const [locationMapActions, setLocationMapActions] = useState<IHomeMatchPreferencesWizardMapActions | null>(null);
    const wasOpen = useRef(false);
    const exitButtonRef = useRef(HomeMatchPreferencesSurveyAmplitudeButton.CLOSE);
    const summaryContentRef = useRef<HTMLDivElement>(null);
    const shouldReduceMotion = useReducedMotion();

    useEffect(() => {
        if (!isOpen) {
            wasOpen.current = false;
            setIsSummaryOpen(false);
            setOpenedFromSummary(false);
            summarySnapshotRef.current = null;
            setParameterStep(null);
            setIsLocationMapOpen(false);
            setLocationMapActions(null);
            return;
        }

        const justOpened = !wasOpen.current;

        wasOpen.current = true;

        if (!justOpened) return;

        reset(defaultValues);
        void refetchHomeMatchPreferences();
        HomeMatchAmplitudeTracker.preferencesSurveyView(location);
    }, [defaultValues, isOpen, location, refetchHomeMatchPreferences, reset]);

    useEffect(() => {
        if (!isOpen || formState.isDirty) return;

        reset(defaultValues);
    }, [defaultValues, formState.isDirty, isOpen, reset]);

    useLayoutEffect(() => {
        const content = summaryContentRef.current;

        if (summaryReturnCount === 0 || !content || shouldReduceMotion || typeof content.animate !== "function") return;

        content.animate(
            [
                {opacity: 0, transform: "translateY(0.8rem)"},
                {opacity: 1, transform: "translateY(0)"}
            ],
            {duration: SUMMARY_RETURN_MS, easing: SUMMARY_RETURN_EASING}
        );
    }, [shouldReduceMotion, summaryReturnCount]);

    const openExit = (button: HomeMatchPreferencesSurveyAmplitudeButton) => {
        exitButtonRef.current = button;
        setIsExitOpen(true);
    };

    const closeExit = () => setIsExitOpen(false);

    const dismissExit = () => {
        HomeMatchAmplitudeTracker.preferencesSurveyClick(exitButtonRef.current, buttonCategory, location);
        onClose();
    };

    const handleAfterClose = () => setIsExitOpen(false);

    const onSubmit = async (form: IHomeMatchPreferencesSurveyForm) => {
        if (!hasHomeMatchRegion(form.regions)) return;

        HomeMatchAmplitudeTracker.preferencesSurveyClick(HomeMatchPreferencesSurveyAmplitudeButton.SAVE, buttonCategory, location);

        try {
            const saved = await patchPreferences(
                buildHomeMatchPreferencesSurveyPatch({
                    additionalExpectationsDirty: Boolean(formState.dirtyFields.additionalExpectations),
                    areaDirty: Boolean(formState.dirtyFields.area),
                    buildingHeightDirty: Boolean(formState.dirtyFields.buildingHeight),
                    constructionEndDateDirty: Boolean(formState.dirtyFields.constructionEndDate),
                    floorsPreferenceDirty: Boolean(formState.dirtyFields.floorsPreference),
                    form,
                    propertyTypeDirty: Boolean(formState.dirtyFields.propertyType),
                    secondaryMarketDirty: Boolean(formState.dirtyFields.secondaryMarket),
                    source: preferencesSurveySource
                })
            ).unwrap();

            HomeMatchAmplitudeTracker.preferencesSurveySaved({
                form,
                screen: location ? {location} : null,
                source: preferencesSurveySource,
                saved: {
                    area: saved.area,
                    construction_end_date: saved.construction_end_date,
                    financing_preference: saved.financing_preference ?? undefined,
                    price: saved.price,
                    rooms: saved.rooms
                }
            });
            reset(form);

            if (homeMatchPreferences?.is_active) {
                onClose();
                dispatch(addNotification({type: "success", content: SAVE_SUCCESS}));
                return;
            }

            setHomeMatchBasicConsent(DEFAULT_HOME_MATCH_BASIC_CONSENT);
            setIsExitOpen(false);
            setIsSummaryOpen(true);
        } catch {
            dispatch(addNotification({type: "failure", content: SAVE_ERROR}));
        }
    };

    const closeSummary = () => {
        setIsSummaryOpen(false);
        onClose();
    };

    const confirmSummary = () => {
        if (isSwitchingHomeMatchPreferences) return;

        void switchHomeMatchPreferences(homeMatchBasicConsent, {
            onPhoneVerificationRequired: () => dispatch(addNotification(defaultAPIErrorNotification)),
            showSuccessNotification: false,
            skipPhoneVerificationPrompt: true,
            sourceSection: getHomeMatchConfigurationSourceSection({
                preferencesSurveySource,
                trigger: HomeMatchConfigurationSourceSectionTrigger.SUMMARY_MODAL
            }),
            syncCplPreferences: false
        }).then((isSaved) => {
            if (isSaved) closeSummary();
        });
    };

    const openSurveyFromSummary = () => {
        summarySnapshotRef.current = methods.getValues();
        setOpenedFromSummary(true);
        setIsSummaryOpen(false);
    };

    const returnToSummary = (button: HomeMatchPreferencesSurveyAmplitudeButton) => {
        HomeMatchAmplitudeTracker.preferencesSurveyClick(button, buttonCategory, location);

        if (summarySnapshotRef.current) reset(summarySnapshotRef.current);

        summarySnapshotRef.current = null;
        setOpenedFromSummary(false);
        setIsExitOpen(false);
        setIsSummaryOpen(true);
        setSummaryReturnCount((count) => count + 1);
    };

    const leaveSurvey = (button: HomeMatchPreferencesSurveyAmplitudeButton, returnsToSummary: boolean) => {
        if (returnsToSummary) {
            returnToSummary(button);
            return;
        }

        if (!isSummaryOpen && !formState.isDirty) {
            exitButtonRef.current = button;
            dismissExit();
            return;
        }

        openExit(button);
    };

    const requestClose = () => leaveSurvey(HomeMatchPreferencesSurveyAmplitudeButton.CLOSE, !isSummaryOpen && openedFromSummary);
    const discardSurvey = () => leaveSurvey(HomeMatchPreferencesSurveyAmplitudeButton.DISCARD, openedFromSummary);

    const editSummaryStep = (step: HomeMatchPreferencesWizardSurveyStep) => setParameterStep(step);

    const closeParameterStep = () => setParameterStep(null);
    const formValues = methods.getValues();
    const summary: IHomeMatchPreferencesWizardSummaryData = {
        additionalAreas: formValues.additionalExpectations,
        area: toOptionalRange(formValues.area),
        buildingHeight: formValues.buildingHeight,
        constructionEndDate: formValues.constructionEndDate,
        floorsPreference: formValues.floorsPreference,
        marketTypes: formValues.secondaryMarket
            ? [HomeMatchPreferencesMarketType.PRIMARY, HomeMatchPreferencesMarketType.SECONDARY]
            : (survey?.market_types ?? null),
        price: toOptionalRange(formValues.price),
        regionDetails,
        regionIds: formValues.regions.map((region) => Number(region.value)),
        regionOptions: formValues.regions,
        rooms: formValues.rooms,
        types: formValues.propertyType == null ? null : [formValues.propertyType]
    };

    return {
        canSave,
        closeExit,
        closeParameterStep,
        confirmSummary,
        discardSurvey,
        dismissExit,
        editSummaryStep,
        handleAfterClose,
        handleSubmit,
        homeMatchBasicConsent,
        isExitOpen,
        isLoading,
        isLocationMapOpen,
        isSaving,
        isSummaryOpen,
        isSwitchingHomeMatchPreferences,
        locationMapActions,
        methods,
        onSubmit,
        openSurveyFromSummary,
        parameterStep,
        parameterStepLocation: getHomeMatchAmplitudeLocationByPreferencesSource(preferencesSurveySource),
        requestClose,
        setHomeMatchBasicConsent,
        setIsLocationMapOpen,
        setLocationMapActions,
        summary,
        summaryContentRef
    };
};
